import {
	ONN_DEVICE_USER_AGENT_PATTERN,
	ONN_SOURCE_TAG_PATTERN,
	QUERY_PARAM_KEYS,
} from './constants';
import { AnalyticsService } from '$lib/services/analytics';
import { UAParser } from 'ua-parser-js';

const CONSTANTS = {
	REGEX: {
		APP_NAME: /appname:([^|]+)/i,
		APP_VERSION: /appversion:([0-9._]+)/,
		APP_STORE_NAME: /store:([^|]+)/i,
	},
	STORE_NAMES: {
		AMAZON: 'amazon app store',
		APPLE: 'apple app store',
		GOOGLE_PLAY: 'google play store',
		SAMSUNG: 'samsung app store',
	},
	PLATFORMS: {
		MAC: 'MAC',
		WINDOWS: 'WIN',
		IOS: 'ios',
	},
	COOKIE_NAMES: {
		LINK_FROM_APP_PLATFORM: 'linkFromAppPlatform',
	},
	URL_PARAMS: {
		LINK_FROM_APP_PLATFORM: 'linkFromAppPlatform',
	},
	MOBILE_KEYWORDS: [
		'Mobi',
		'Android',
		'iPhone',
		'iPad',
		'iPod',
		'BlackBerry',
		'IEMobile',
		'Opera Mini',
	],
	IOS_KEYWORDS: ['iPhone', 'iPad', 'iPod'],
};

/**
 * Checks if the user is using a Safari browser
 * @returns {boolean} True if the user is using a Safari browser, false otherwise
 */
function isSafari() {
	if (typeof navigator === 'undefined') return false;

	const ua = navigator?.userAgent || '';
	const vendor = navigator?.vendor || '';

	const isIOS =
		/\b(iPad|iPhone|iPod)\b/.test(ua) ||
		(navigator?.platform === 'MacIntel' && navigator?.maxTouchPoints > 1);

	const isWebKit = /AppleWebKit/i.test(ua);
	const isChromeLike = /\b(?:Chrome|CriOS|Edg|EdgiOS|OPR|OPiOS|Brave)\b/i.test(ua);

	const isSafariDesktop =
		!isIOS && /\bSafari\b/i.test(ua) && /Apple/i.test(vendor) && !isChromeLike;

	const isSafariIOS =
		isIOS &&
		isWebKit &&
		/\bSafari\b/i.test(ua) &&
		!/\b(?:CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo|Orion|Aloha)\b/i.test(ua);

	return isSafariDesktop || isSafariIOS;
}

/**
 * Checks if the user is using a desktop browser
 * @returns {boolean} True if the user is using a desktop browser, false otherwise
 */
function isDesktop() {
	if (typeof navigator === 'undefined') return false;

	const ua = navigator?.userAgent || '';
	const platform = (navigator?.platform || '').toUpperCase();
	const maxTouchPoints = navigator?.maxTouchPoints || 0;

	const isMac = platform.includes('MAC');
	const isWindows = platform.includes('WIN');
	const isLinux = platform.includes('LINUX') && !/Android/i.test(ua);

	// Detect iPadOS masquerading as Mac
	const isIPadOS = isMac && maxTouchPoints > 1;

	return (isMac || isWindows || isLinux) && !isIPadOS;
}

/**
 * Constructs meta information about the user based on various parameters.
 * @param {object} options - The options object to construct the meta information.
 * @param {Navigator} options.navigator - The user agent string, typically from `navigator`.
 * @param {URLSearchParams} options.urlSearchParams - An instance of `URLSearchParams` for parsing the URL query parameters.
 * @returns {Promise<{[key:string]: string | boolean | null}>} An object containing various flags and extracted values related to the user's environment.
 */
async function userDetectionService({ navigator, urlSearchParams = new URLSearchParams() }) {
	const { device } = await UAParser().withFeatureCheck();

	const appNameMatches = CONSTANTS.REGEX.APP_NAME.exec(navigator.userAgent);
	const appVersionMatches = CONSTANTS.REGEX.APP_VERSION.exec(navigator.userAgent);
	const appStoreNameMatches = CONSTANTS.REGEX.APP_STORE_NAME.exec(navigator.userAgent);

	const appName = appNameMatches ? appNameMatches[1] : null;
	const appVersion = appVersionMatches ? appVersionMatches[1] : null;
	const appStoreName = appStoreNameMatches ? appStoreNameMatches[1] : null;

	const isAndroidApp = Boolean(
		appStoreName?.toLowerCase().includes(CONSTANTS.STORE_NAMES.GOOGLE_PLAY)
	);
	const isSmartTvApp = Boolean(navigator.userAgent?.toLowerCase().includes('smarttv'));

	const isAmazonApp = appStoreName?.toLowerCase() === CONSTANTS.STORE_NAMES.AMAZON;
	const isIosApp = appStoreName?.toLowerCase() === CONSTANTS.STORE_NAMES.APPLE;

	const appStoreNameFromQueryString = urlSearchParams.get(QUERY_PARAM_KEYS.APP_STORE_NAME);

	const isSamsungApp = appStoreNameFromQueryString
		? appStoreNameFromQueryString.toLowerCase() === CONSTANTS.STORE_NAMES.SAMSUNG
		: appStoreName?.toLowerCase() === CONSTANTS.STORE_NAMES.SAMSUNG;

	const isLinkFromEmail = Boolean(urlSearchParams.get(QUERY_PARAM_KEYS.TARGET_AOFL_PRODUCT_UUID));

	const isMac = navigator.platform?.toUpperCase().includes(CONSTANTS.PLATFORMS.MAC);
	const isWindows = navigator.platform?.toUpperCase().includes(CONSTANTS.PLATFORMS.WINDOWS);

	// TODO: consider if this method is accurate over cloudfront headers
	const isDesktopViewer = isDesktop();

	// Check if User-Agent contains mobile keywords
	const hasMobileKeywords = CONSTANTS.MOBILE_KEYWORDS.some((keyword) =>
		navigator.userAgent.includes(keyword)
	);

	// Also detect iOS devices in "Request Desktop Website" mode
	// When desktop mode is enabled, User-Agent won't have mobile keywords,
	// but we can detect iOS via platform or maxTouchPoints
	// Fixes ABC-25523
	const platform = navigator.platform || '';
	const maxTouchPoints = navigator.maxTouchPoints || 0;
	const isIOSDevice =
		/\b(iPad|iPhone|iPod)\b/i.test(platform) || (platform === 'MacIntel' && maxTouchPoints > 1);

	const isMobileViewer = hasMobileKeywords || isIOSDevice;

	const isAmazonViewer = /amazon|silk|KF[A-Z]{4,}/i.test(navigator.userAgent);
	const isAndroidViewer = Boolean(navigator.userAgent?.toLowerCase().includes('android'));
	const isIosViewer = CONSTANTS.IOS_KEYWORDS.some((keyword) =>
		navigator.userAgent.includes(keyword)
	);
	const isTabletViewer = device.is('tablet');

	const isSafariBrowser = isSafari();

	const browserPlatformMap = {
		safari: isSafariBrowser,
		default_browser: !isSafariBrowser,
		// Add additional browser platform detection here
	};

	const appStoreMap = {
		google: isAndroidApp,
		lgtv: isSmartTvApp,
		ios: isIosApp,
		samsung: isSamsungApp,
		amazon: isAmazonApp,
	};
	const appStore = Object.entries(appStoreMap).find(([_, value]) => value)?.[0] || null;

	const acquisitionSource = AnalyticsService.getSourceTag() ?? 'none';
	const isOnnDevice =
		ONN_SOURCE_TAG_PATTERN.test(acquisitionSource) ||
		ONN_DEVICE_USER_AGENT_PATTERN.test(navigator.userAgent);
	const browserPlatform =
		Object.entries(browserPlatformMap).find(([_, value]) => value)?.[0] || null;

	const isAutomatedTesting = navigator.userAgent.includes('isAutomatedTesting:true');

	const config = {
		inApp: Boolean(appName || appVersion || appStoreName),
		browserPlatform: browserPlatform,
		acquisitionSource: acquisitionSource,
		appName: appName,
		appVersion: appVersion,
		appStoreName: appStoreName,
		appStore: appStore,
		isAndroidApp: isAndroidApp,
		isSmartTvApp: isSmartTvApp,
		isIosApp: isIosApp,
		isSamsungApp: isSamsungApp,
		isAmazonViewer: isAmazonViewer,
		isOnnDevice: isOnnDevice,
		isMac: isMac,
		isWindows: isWindows,
		isMobileViewer: isMobileViewer,
		isDesktopViewer: isDesktopViewer,
		isAmazonApp: isAmazonApp,
		isAndroidViewer: isAndroidViewer,
		isIosViewer: isIosViewer,
		isTabletViewer: isTabletViewer,
		isLinkFromEmail: isLinkFromEmail,
		isAutomatedTesting: isAutomatedTesting,
	};

	const viewerCountry = urlSearchParams.get(QUERY_PARAM_KEYS.VIEWER_COUNTRY);
	const viewerCountryRegion = urlSearchParams.get(QUERY_PARAM_KEYS.VIEWER_COUNTRY_REGION);

	if (viewerCountry || viewerCountryRegion) {
		return {
			...config,
			...(viewerCountry && { viewerCountry }),
			...(viewerCountryRegion && { viewerCountryRegion }),
		};
	}

	return config;
}

export { userDetectionService };
